import React, { useEffect, useState, useRef } from 'react';
import api from '../utils/axios';
import { useNavigate, Link } from 'react-router-dom';
import { monthNames } from '../constants';
import { calculateTaxFromDatabase } from '../constants/tax';
import { calculateTransactionFee, formatTransactionFee } from '../utils/transactionFees';
import Dropdown from '../components/Dropdown';
import PermissionGate from '../components/PermissionGate';
import { useAuth } from '../contexts/AuthContext';
import { canCreateSalarySheet, canReachReports, canUpdateSalarySheet } from '../utils/reportPermissions';
import useLatestRequest from '../hooks/useLatestRequest';
import CModal from '../components/shared/c-modal';
import { companyToday } from '../utils/dateUtils';
import { fiscalStartYear, fiscalYearLabel } from '../utils/fiscalYear';

// The company's current month: a payroll month, whatever zone the browser is in.
const getCurrentMonthYear = () => {
  const [year, month] = companyToday().split('-').map(Number);
  return { month, year };
};

const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();

// The header's inputs take the Month dropdown's box, so the row lines up.
const headerFieldClass = 'border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

// Calculated Salary (PKR): the monthly salary prorated by the days worked.
const calculatedSalary = (salaryPkr, worked, totalDays) =>
  (worked > 0 && totalDays > 0 ? (salaryPkr * worked) / totalDays : 0);

// The manual PKR amount is paid out of the row's Calculated Salary, so it cannot be more.
const manualPkrErrorFor = (amount, calcSalary) =>
  (Number(amount) > calcSalary ? 'Manual PKR cannot exceed Calculated Salary (PKR)' : undefined);

// Server codes meaning the tax year's slabs need HR before any row can be taxed. Listed rather
// than matched on the TAX_SLABS_ prefix: each needs its own words (add them, fix them), and a
// code this page has no words for reads better as a plain failure than in another code's words.
const SLAB_FAILURES = new Map([['TAX_SLABS_MISSING', 'slabs_missing'], ['TAX_SLABS_INCOMPLETE', 'slabs_incomplete']]);

const NewSalarySheetReport = () => {
  const [month, setMonth] = useState(getCurrentMonthYear().month);
  const [year, setYear] = useState(getCurrentMonthYear().year);
  const [usdRate, setUsdRate] = useState('');
  const [employees, setEmployees] = useState([]);
  const [manualPkr, setManualPkr] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The month did not load: what the sheet holds is not that month's data, so nothing may be
  // saved from it until a load succeeds. Only "Try again" (reloadCount) or a period change reloads.
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [daysWorked, setDaysWorked] = useState({});
  const [taxCalculations, setTaxCalculations] = useState({});
  const [taxLoading, setTaxLoading] = useState({});
  // Employee id -> why the row's tax failed, as { kind, message }: kind is 'slabs_missing' or
  // 'slabs_incomplete' when the tax year's slabs are the trouble, else 'failed'; message is the
  // server's. Falsy once it succeeds.
  const [taxFailed, setTaxFailed] = useState({});
  const [transactionFees, setTransactionFees] = useState({});
  const [feeConfig, setFeeConfig] = useState(null);
  const navigate = useNavigate();
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [pendingReportData, setPendingReportData] = useState(null);
  const [existingReportId, setExistingReportId] = useState(null);
  const { hasPermission } = useAuth();
  // A month that already has a sheet can only be overwritten (PUT, reports:update); without
  // that grant the page says so instead of offering a save the server refuses.
  const overwriteBlocked = Boolean(existingReportId) && !canUpdateSalarySheet(hasPermission);
  const fiscalStart = fiscalStartYear(month, year);
  const taxDebounceTimers = useRef({});
  // Employee id -> the one tax request that may still write that row.
  const latestTaxRequest = useRef({});
  const taxRequestCount = useRef(0);

  // Fetch transaction fee configuration
  useEffect(() => {
    const fetchFeeConfig = async () => {
      try {
        const res = await api.get('/api/transaction-fees');
        const config = res.data.data.map(row => ({
          minPkr: Number.parseFloat(row.min_pkr),
          maxPkr: row.max_pkr === null ? Infinity : Number.parseFloat(row.max_pkr),
          usdFee: Number.parseFloat(row.usd_fee)
        }));
        setFeeConfig(config);
      } catch (err) {
        console.error('Failed to fetch transaction fee config:', err);
        // Use default config if API fails
        setFeeConfig([
          { minPkr: 100000, maxPkr: 150000, usdFee: 10 },
          { minPkr: 150001, maxPkr: 200000, usdFee: 15 },
          { minPkr: 200001, maxPkr: 250000, usdFee: 20 },
          { minPkr: 250001, maxPkr: 300000, usdFee: 25 },
          { minPkr: 300001, maxPkr: 350000, usdFee: 30 },
          { minPkr: 350001, maxPkr: Infinity, usdFee: 30 }
        ]);
      }
    };
    fetchFeeConfig();
  }, []);

  // Works out one row's tax for the amount now in it, after `delayMs` (typing waits for a
  // pause). Only the row's newest request may write its result or clear its spinner: an
  // older one can answer after it, and a month switch drops them all. The row is pending
  // from this call on, so Save waits for this amount's tax instead of saving the last one's.
  // It reads only refs, setters and its arguments: the load effect and async continuations
  // call it without listing it as a dependency, so any state read here would be stale.
  const calculateRowTax = (emp, amount, fiscalStart, delayMs = 0) => {
    clearTimeout(taxDebounceTimers.current[emp.id]);
    const request = ++taxRequestCount.current;
    latestTaxRequest.current[emp.id] = request;
    const settle = (tax, failure) => {
      if (latestTaxRequest.current[emp.id] !== request) return;
      if (tax) setTaxCalculations(prev => ({ ...prev, [emp.id]: tax }));
      setTaxFailed(prev => ({ ...prev, [emp.id]: tax ? false : failure }));
      setTaxLoading(prev => ({ ...prev, [emp.id]: false }));
    };
    // Nothing to ask the server; a request still out for the row's old amount is dropped.
    if (!emp.taxable || !(amount > 0)) {
      settle({ monthlyTax: 0, afterTax: amount });
      return;
    }
    setTaxLoading(prev => ({ ...prev, [emp.id]: true }));
    setTaxFailed(prev => ({ ...prev, [emp.id]: false }));
    const send = async () => {
      let tax = null;
      let failure;
      try {
        const { monthlyTax } = await calculateTaxFromDatabase(amount * 12, fiscalStart);
        tax = { monthlyTax, afterTax: amount - monthlyTax };
      } catch (err) {
        // The row says so and Save stays blocked until a retry succeeds.
        const { code, message } = err.response?.data || {};
        failure = { kind: SLAB_FAILURES.get(code) || 'failed', message: typeof message === 'string' ? message : '' };
      }
      settle(tax, failure);
    };
    if (delayMs) taxDebounceTimers.current[emp.id] = setTimeout(send, delayMs);
    else send();
  };

  // Recalculate every employee's transaction fee, on the Calculated Salary (PKR), when
  // anything it is computed from changes.
  useEffect(() => {
    if (!feeConfig) return;

    const fees = {};
    employees.forEach(emp => {
      const totalDays = getDaysInMonth(month, year);
      const calcSalary = calculatedSalary(emp.salary_pkr, Number(daysWorked[emp.id]) || 0, totalDays);
      fees[emp.id] = calculateTransactionFee(calcSalary, feeConfig);
    });

    setTransactionFees(fees);
  }, [feeConfig, employees, daysWorked, month, year]);

  // "Replicate Previous Month" copies the month before the one showing. Picking another
  // month or year while it loads must drop it, or the old month's amounts merge into the
  // new sheet and get saved there. Every period change begins a new token below.
  const beginReplicate = useLatestRequest();

  useEffect(() => {
    // Only the latest run may fill the sheet: StrictMode runs this effect twice in dev,
    // and switching month or year starts a new load. A late response would otherwise
    // overwrite the amounts typed since, or load another month's report into this one.
    let cancelled = false;
    beginReplicate();
    const fetchEmployeesAndMaybeReport = async () => {
      setLoading(true);
      setError('');
      setLoadFailed(false);
      // A dialog opened on the previous sheet must not confirm a save into this one.
      setShowOverwriteModal(false);
      setPendingReportData(null);
      setTaxCalculations({});
      setTaxLoading({});
      setTaxFailed({});
      // Forget the previous month's sheet first: if this month's lookup fails, a stale id
      // would send this month's rows to the other month's report on Save.
      setExistingReportId(null);
      try {
        const res = await api.get('/api/employees/with-latest-salary');
        if (cancelled) return;
        
        // The API returns one row per (employee, currency). Collapse into
        // one entry per employee, picking up the PKR bank-transfer salary.
        const empMap = {};
        for (const row of res.data.data) {
          const eid = row.id;
          if (!empMap[eid]) {
            empMap[eid] = {
              id: eid,
              name: row.first_name + ' ' + row.last_name,
              employee_id: row.employee_id,
              salary_pkr: 0,
              taxable: row.taxable
            };
          }
          // Only count PKR salaries paid via bank transfer
          if (
            row.salary_currency === 'PKR' &&
            row.payment_method_name &&
            row.payment_method_name.toLowerCase().includes('bank')
          ) {
            empMap[eid].salary_pkr = Number(row.salary_amount) || 0;
          }
        }
        const emps = Object.values(empMap);
        
        setEmployees(emps);
        const totalDays = getDaysInMonth(month, year);
        setDaysWorked(Object.fromEntries(emps.map(e => [e.id, totalDays])));
        setManualPkr(Object.fromEntries(emps.map(e => [e.id, 0])));

        // Check for existing report
        const reportRes = await api.get(`/api/reports?type=salary_sheet&month=${month}&year=${year}`);
        if (cancelled) return;
        const existing = reportRes.data.data[0];
        if (existing) {
          setExistingReportId(existing.id);
          const detail = await api.get(`/api/reports/${existing.id}`);
          if (cancelled) return;
          const rows = detail.data.data.rows || [];
          // Set USD rate from meta if present
          if (detail.data.data.meta && detail.data.data.meta.usd_rate) {
            setUsdRate(detail.data.data.meta.usd_rate);
          }
          // Populate manualPkr and daysWorked from report rows
          const manual = {};
          const days = {};
          for (const row of rows) {
            manual[row.employee_id] = row.data.manual_pkr || 0;
            days[row.employee_id] = row.data.days_worked || totalDays;
          }
          setManualPkr(mp => ({ ...mp, ...manual }));
          setDaysWorked(dw => ({ ...dw, ...days }));
          // The sheet's amounts are in: each row's tax, worked out once from these values.
          const fiscalStart = fiscalStartYear(month, year);
          for (const emp of emps) calculateRowTax(emp, Number(manual[emp.id]) || 0, fiscalStart);
        } else {
          setExistingReportId(null);
        }
      } catch {
        if (!cancelled) setLoadFailed(true);
      }
      if (!cancelled) setLoading(false);
    };
    fetchEmployeesAndMaybeReport();
    return () => {
      cancelled = true;
      // The sheet is being replaced, or the page left: no tax still on its way for the old
      // sheet may land in the new one, and no pending recalculation may fire into it.
      Object.values(taxDebounceTimers.current).forEach(clearTimeout);
      taxDebounceTimers.current = {};
      latestTaxRequest.current = {};
    };
  }, [month, year, beginReplicate, reloadCount]);

  const replicatePreviousMonth = async () => {
    const isCurrent = beginReplicate();
    setError('');
    try {
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const res = await api.get(`/api/reports?type=salary_sheet&month=${prevMonth}&year=${prevYear}`);
      if (!isCurrent()) return;
      const prevReport = res.data.data[0];
      if (!prevReport) {
        setError('No previous month report found.');
        return;
      }
      const reportDetail = await api.get(`/api/reports/${prevReport.id}`);
      if (!isCurrent()) return;
      const prevRows = reportDetail.data.data.rows;
      const prevManual = {};
      for (const row of prevRows) {
        prevManual[row.employee_id] = row.data.manual_pkr || 0;
      }
      setManualPkr(mp => ({ ...mp, ...prevManual }));
      // Those rows' amounts were just replaced, and their tax with them: a request or a
      // pending recalculation for an amount they no longer hold is dropped. A row the previous
      // month's sheet lacks keeps its amount, so it keeps its tax too.
      for (const emp of employees) {
        if (!(emp.id in prevManual)) continue;
        calculateRowTax(emp, Number(prevManual[emp.id]) || 0, fiscalStart);
      }
    } catch (err) {
      if (isCurrent()) setError('Failed to replicate previous month.');
    }
  };

  const handleManualPkrChange = (id, value) => {
    setManualPkr(mp => ({ ...mp, [id]: value }));
    // Only the changed row, once typing pauses.
    calculateRowTax(employees.find(e => e.id === id), Number(value), fiscalStart, 300);
  };

  const retryFailedTax = () => {
    for (const emp of employees) {
      if (taxFailed[emp.id]) calculateRowTax(emp, Number(manualPkr[emp.id]) || 0, fiscalStart);
    }
  };

  const taxPending = Object.values(taxLoading).some(Boolean);
  const taxFailures = Object.values(taxFailed).filter(Boolean);
  const taxFailedCount = taxFailures.length;

  // A tax year whose slabs need HR is the one to name: every other failed row would hit it on Retry too.
  const taxFailureText = () => {
    const fiscalYear = <span className="whitespace-nowrap">{fiscalYearLabel(fiscalStart)}</span>;
    if (taxFailures.some(f => f.kind === 'slabs_missing')) {
      return <>No tax slabs are set up for fiscal year {fiscalYear}, so tax cannot be calculated. HR needs to add them before this salary sheet can be saved.</>;
    }
    const incomplete = taxFailures.find(f => f.kind === 'slabs_incomplete');
    if (incomplete) {
      // The server's message says what is wrong with them.
      const what = incomplete.message || <>The tax slabs for fiscal year {fiscalYear} are incomplete, so tax cannot be calculated.</>;
      return <>{what} HR needs to fix them before this salary sheet can be saved.</>;
    }
    return `Tax could not be calculated for ${taxFailedCount} ${taxFailedCount === 1 ? 'employee' : 'employees'}, so the report cannot be saved yet.`;
  };

  // Worked out on every render, not stored: an amount is checked against its row's Calculated
  // Salary however either got there (typed, copied by Replicate, loaded from a saved sheet,
  // or a Days Worked change after it).
  const manualPkrError = Object.fromEntries(employees.map(emp => [
    emp.id,
    manualPkrErrorFor(
      manualPkr[emp.id],
      calculatedSalary(emp.salary_pkr, Number(daysWorked[emp.id]) || 0, getDaysInMonth(month, year)),
    ),
  ]));

  // Everything that stops a save, read by both the button and handleSave so the two cannot
  // disagree. A manual PKR error is not here: Save stays clickable and says what to fix.
  const saveBlocked = overwriteBlocked || saving || loading || loadFailed || !usdRate || taxPending || taxFailedCount > 0;

  const handleDaysWorkedChange = (id, value) => {
    setDaysWorked(dw => ({ ...dw, [id]: value }));
  };

  const handleSave = async () => {
    if (saveBlocked) return;
    if (Object.values(manualPkrError).some(Boolean)) {
      setError('Please fix manual PKR errors before saving.');
      return;
    }
    setSaving(true);
    setError('');
    const rows = employees.map(emp => {
      const totalDays = getDaysInMonth(month, year);
      const worked = Number(daysWorked[emp.id]) || 0;
      const calcSalary = calculatedSalary(emp.salary_pkr, worked, totalDays);
      const manualAmount = Number(manualPkr[emp.id]) || 0;
      const pkrToBePaidInUsd = calcSalary - manualAmount;
      const grossUsdAmount = (pkrToBePaidInUsd / (Number(usdRate) || 1));
      const transactionFee = transactionFees[emp.id] || 0;
      const netUsdAmount = Math.max(0, grossUsdAmount - transactionFee);
      return {
        employee_id: emp.id,
        data: {
          name: emp.name,
          employee_id: emp.employee_id,
          total_days: totalDays,
          days_worked: worked,
          salary_pkr: emp.salary_pkr,
          calculated_salary_pkr: calcSalary,
          manual_pkr: manualAmount,
          monthlyTax: taxCalculations[emp.id]?.monthlyTax || 0,
          monthly_tax_withheld: taxCalculations[emp.id]?.monthlyTax || 0,
          pkr_to_be_paid_in_usd: pkrToBePaidInUsd,
          gross_usd_amount: grossUsdAmount,
          transaction_fee_usd: transactionFee,
          net_usd_amount: netUsdAmount
        }
      };
    });
    const reportData = {
      type: 'salary_sheet',
      month,
      year,
      title: `Salary Sheet Report - ${monthNames[month-1]} ${year}`,
      usd_rate: Number(usdRate),
      rows
    };
    if (existingReportId) {
      setPendingReportData(reportData);
      setShowOverwriteModal(true);
      setSaving(false);
      return;
    }
    try {
      await api.post('/api/reports', reportData);
      navigate('/reports');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save report');
    }
    setSaving(false);
  };

  const handleOverwrite = async () => {
    // The rows were built for the period the dialog was opened on, and the server's update does
    // not check them against the report's own. A load closes the dialog; this is the backstop.
    if (!existingReportId || pendingReportData?.month !== month || pendingReportData?.year !== year) return;
    setSaving(true);
    setError('');
    try {
      await api.put(`/api/reports/${existingReportId}`, pendingReportData);
      setShowOverwriteModal(false);
      setPendingReportData(null);
      setExistingReportId(null);
      navigate('/reports');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to overwrite report');
    }
    setSaving(false);
  };

  return (
    <div className="w-full mx-auto px-0 py-6">
      <h2 className="text-2xl font-bold mb-4">Generate Salary Sheet Report</h2>
      {error && <div className="mb-4 text-red-600">{error}</div>}
      {/* Overwrite Modal */}
      {showOverwriteModal && (
        <CModal
          onClose={() => setShowOverwriteModal(false)}
          labelledBy="overwrite-report-title"
          dismissible={!saving}
          overlayClassName="bg-black/40"
          className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full"
        >
          <h3 id="overwrite-report-title" className="text-lg font-semibold mb-4 text-red-600">Overwrite Existing Report?</h3>
          <p className="mb-4">A report for {monthNames[month-1]} {year} already exists. Overwriting will replace all data for this month. Are you sure you want to continue?</p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 bg-gray-200 rounded"
              onClick={() => setShowOverwriteModal(false)}
              disabled={saving}
            >Cancel</button>
            <button
              className="px-4 py-2 bg-red-600 text-white rounded"
              onClick={handleOverwrite}
              disabled={saving}
            >{saving ? 'Overwriting...' : 'Overwrite'}</button>
          </div>
        </CModal>
      )}
      <div className="flex flex-wrap gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium">Month</label>
          <Dropdown value={month} onChange={e => setMonth(Number(e.target.value))} data-testid="reports-field-month">
            {monthNames.map((name, i) => (
              <option key={i+1} value={i+1}>{name}</option>
            ))}
          </Dropdown>
        </div>
        <div>
          <label htmlFor="salary-sheet-year" className="block text-sm font-medium">Year</label>
          <input id="salary-sheet-year" type="number" value={year} onChange={e => setYear(Number(e.target.value))} onWheel={(e) => e.target.blur()} data-testid="reports-field-year" className={`${headerFieldClass} w-24`} />
        </div>
        <div>
          <label htmlFor="salary-sheet-fiscal-year" className="block text-sm font-medium">
            Fiscal Year <span className="font-normal text-gray-500">(July to June)</span>
          </label>
          {/* Follows from the month and year, so it is shown, not chosen. */}
          <input id="salary-sheet-fiscal-year" type="text" readOnly value={fiscalYearLabel(fiscalStart)} data-testid="reports-field-fiscal-year" className={`${headerFieldClass} w-full bg-gray-100 text-gray-700`} />
        </div>
        <div>
          <label htmlFor="salary-sheet-usd-rate" className="block text-sm font-medium">USD Rate</label>
          <input id="salary-sheet-usd-rate" type="number" value={usdRate} onChange={e => setUsdRate(e.target.value)} onWheel={(e) => e.target.blur()} data-testid="reports-field-usd-rate" className={`${headerFieldClass} w-24`} />
        </div>
        {/* Not while the month loads: the load would overwrite the copied amounts. Nor after it
            failed: there is no sheet to copy them into. */}
        <button type="button" className="self-end px-4 py-2 border border-transparent bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:hover:bg-gray-200" onClick={replicatePreviousMonth} disabled={loading || loadFailed}>Replicate Previous Month</button>
      </div>
      {!loading && overwriteBlocked && (
        <div data-testid="reports-sheet-exists" className="bg-warning-50 border border-warning-200 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-warning-800">
              A salary sheet for {monthNames[month - 1]} {year} already exists.
            </p>
            {/* warning-700, not the Dashboard banner's 600: 600 on warning-50 is about 3:1. */}
            <p className="text-xs text-warning-700 mt-0.5">
              Changing it needs permission to update reports, which your role does not have.
            </p>
          </div>
          {canReachReports(hasPermission) && (
            <Link
              to={`/reports/${existingReportId}`}
              className="text-sm font-medium text-warning-700 hover:text-warning-900 underline ml-4 shrink-0"
            >
              View it
            </Link>
          )}
        </div>
      )}
      {loading && <div>Loading employees...</div>}
      {!loading && loadFailed && (
        <div role="alert" className="text-sm text-red-600 mt-6">
          The salary sheet for {monthNames[month - 1]} {year} could not be loaded, so the report cannot be saved yet.{' '}
          <button type="button" onClick={() => setReloadCount(n => n + 1)} className="font-medium underline hover:text-red-800">Try again</button>
        </div>
      )}
      {!loading && !loadFailed && (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total Days</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Days Worked</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Salary (PKR)</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Calculated Salary (PKR)</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PKR Amount (Manual)</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Tax Withheld</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PKR Salary After Tax</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">PKR to be paid in USD</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">USD Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross USD Amount</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Fee (USD)</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net USD Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.map(emp => {
                  const totalDays = getDaysInMonth(month, year);
                  const calcSalary = calculatedSalary(emp.salary_pkr, Number(daysWorked[emp.id]) || 0, totalDays);
                  const manualAmount = Number(manualPkr[emp.id]) || 0;
                  const taxData = taxCalculations[emp.id] || { monthlyTax: 0, afterTax: manualAmount };
                  const isLoading = taxLoading[emp.id] || false;
                  // Both tax cells: a spinner while the row's tax is on its way, a note if it failed.
                  const renderTax = (value) => {
                    if (isLoading) return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto"></div>;
                    if (taxFailed[emp.id]) return <span className="text-xs font-medium text-red-600 whitespace-nowrap">Not calculated</span>;
                    return value;
                  };
                  const pkrToBePaidInUsd = calcSalary - manualAmount;
                  const grossUsdAmount = (pkrToBePaidInUsd / (Number(usdRate) || 1));
                  const transactionFee = transactionFees[emp.id] || 0;
                  const netUsdAmount = Math.max(0, grossUsdAmount - transactionFee);
                  
                  return (
                    <tr key={emp.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">{emp.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{emp.employee_id}</td>
                      <td className="px-6 py-4 text-center">{totalDays}</td>
                      <td className="px-6 py-4 text-center">
                        <input
                          type="number"
                          min="0"
                          max={totalDays}
                          value={daysWorked[emp.id]}
                          onChange={e => handleDaysWorkedChange(emp.id, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className="w-16 border rounded px-2 py-1 text-center focus:ring-primary-500 focus:border-primary-500"
                        />
                      </td>
                      <td className="px-6 py-4 text-right">{emp.salary_pkr.toLocaleString()}</td>
                      <td className="px-6 py-4 text-right">{calcSalary.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-6 py-4 text-center">
                        <input
                          type="number"
                          min="0"
                          max={calcSalary}
                          value={manualPkr[emp.id]}
                          onChange={e => handleManualPkrChange(emp.id, e.target.value)}
                          onWheel={e => e.target.blur()}
                          className={`w-24 border rounded px-2 py-1 text-center focus:ring-primary-500 focus:border-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${manualPkrError[emp.id] ? 'border-red-500' : ''}`}
                        />
                        {manualPkrError[emp.id] && <div className="text-xs text-red-600 mt-1">{manualPkrError[emp.id]}</div>}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {renderTax(!emp.taxable ? '0' : (manualAmount > 0 && taxData.monthlyTax !== undefined
                          ? taxData.monthlyTax.toLocaleString()
                          : ''))}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {renderTax(!emp.taxable ? manualAmount.toLocaleString() : (manualAmount > 0 && taxData.afterTax !== undefined
                          ? taxData.afterTax.toLocaleString()
                          : ''))}
                      </td>
                      <td className="px-6 py-4 text-right">{pkrToBePaidInUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-6 py-4 text-right">{usdRate}</td>
                      <td className="px-6 py-4 text-right">{grossUsdAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-6 py-4 text-right text-sm">
                        <span className="text-gray-600">{formatTransactionFee(transactionFee)}</span>
                        {transactionFee > 0 && (
                          <div className="text-xs text-gray-400">
                            {calcSalary >= 100000 ? 
                              `PKR ${(calcSalary/1000).toFixed(0)}K+` : 
                              'Below threshold'
                            }
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-primary-600">
                        {netUsdAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!overwriteBlocked && (
        <button
          data-testid="reports-generate"
          className="mt-4 px-6 py-2 bg-primary-600 text-white rounded font-semibold disabled:opacity-50"
          onClick={handleSave}
          disabled={saveBlocked}
        >
          {saving ? 'Saving...' : 'Save Report'}
        </button>
      )}
      {taxPending && (
        <div className="text-sm text-yellow-600 mt-2">Please wait for all tax calculations to finish before saving.</div>
      )}
      {taxFailedCount > 0 && (
        <div role="alert" className="text-sm text-red-600 mt-2">
          {taxFailureText()}{' '}
          <button type="button" onClick={retryFailedTax} className="font-medium underline hover:text-red-800">Retry</button>
        </div>
      )}
    </div>
  );
};

// A salary sheet is pay: without reports:create + salaries:read_all every call here 403s.
const GatedNewSalarySheetReport = () => (
  <PermissionGate
    check={canCreateSalarySheet}
    message="You do not have permission to create salary sheets"
    redirectTo="/reports/new"
  >
    <NewSalarySheetReport />
  </PermissionGate>
);

export default GatedNewSalarySheetReport; 